'use client';

import { useState } from 'react';
import { TONE, type Tone } from '@leanin/ui';

import { toneFromId } from '../../lib/group-tone';

const FALLBACK_TONE_CYCLE: ReadonlyArray<Tone> = [
  'burgundy',
  'yellow',
  'emerald',
  'navy',
  'orange',
];

// Brand block for any group (circle / network / space). Renders the uploaded
// image when present; otherwise an id-derived tonal background with the
// group's italic initials. There is no user-facing "pick a tone" affordance —
// the fallback tone is purely a deterministic visual variety knob.
//
// If the upload fails to load (404, ORB-blocked CDN response, broken link), the
// component falls back to the initials block — without this, the browser
// renders the literal alt text in the empty <img> rect, which reads as broken.
export function GroupLogoBlock({
  logoUrl,
  groupId,
  name,
  size = 96,
}: {
  logoUrl: string | null;
  groupId: string;
  name: string;
  size?: 96 | 72 | 48;
}) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const fontSize = size === 96 ? 32 : size === 72 ? 24 : 16;

  if (logoUrl && !imgFailed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          flexShrink: 0,
          border: '1px solid var(--border)',
        }}
      >
        <img
          src={logoUrl}
          alt={name}
          width={size}
          height={size}
          onError={() => setImgFailed(true)}
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        />
      </div>
    );
  }

  const tone = toneFromId(groupId, FALLBACK_TONE_CYCLE);
  const swatch = TONE[tone];

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 'var(--radius-md)',
        background: swatch.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        font: `italic 400 ${fontSize}px/1 var(--font-serif)`,
        color: swatch.ink,
      }}
    >
      {initials}
    </div>
  );
}
